import { Button, ButtonProps, Card, CardProps, Collapsible, Icon } from '@shopify/polaris';
import { ArrowLeftIcon, ChevronDownIcon, ChevronUpIcon } from '@shopify/polaris-icons';
import React, { ReactElement, memo, useState } from 'react';
import BoldText from '../BoldText';
import Required from '../Required';
import { CardStyled } from './styled';
import { BREAKPOINT } from '@/constants/enum';
import { useMediaQuery } from 'react-responsive';

interface Props {
  hasDroplist?: boolean;
  title?: string | ReactElement;
  required?: boolean;
  secondaryActions?: {
    content?: any;
    buttonProps?: ButtonProps;
    onAction?: () => void;
  };
  backAction?: () => void;
  children?: any;
  display?: string;
  disableCardBorder?: boolean;
  titleContentRight?: ReactElement;
  textClassName?: string;
  badgeHeader?: React.ReactNode;
  isHighlight?: boolean;
  isTitleFullWidth?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  padding?: CardProps['padding'];
  badgeTop?: React.ReactNode;
  style?: React.CSSProperties;
  onHover?: (isIn: boolean) => void;
  hideBoxShadow?: boolean;
  showBorderBottom?: boolean;
  isResponsiveAction?: boolean;
  cardClassName?: string;
}

const CustomCard = ({ ...props }: Props) => {
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.SM });
  const [isCollapsed, setIsCollapsed] = useState(props.defaultExpanded || !props.collapsible);
  return (
    <CardStyled
      className={`custom-card ${props.cardClassName}`}
      isHighlight={props.isHighlight}
      isTitleFullWidth={props.isTitleFullWidth}
      hasDroplist={props.hasDroplist}
      style={{ display: props.display || 'block', ...props.style }}
      onMouseEnter={() => props.onHover?.(true)}
      onMouseLeave={() => props.onHover?.(false)}
      hideBoxShadow={props.hideBoxShadow}
      showBorderBottom={props.showBorderBottom}
    >
      {!props.disableCardBorder ? (
        <>
          {props.badgeTop ? <div className="custom-card-badge-top">{props.badgeTop}</div> : null}
          <div className={`${props.badgeTop ? 'yellow-border' : ''} h-full`}>
            <Card padding={props.padding || '400'}>
              <div className="custom-card-header">
                {props.title ? (
                  <div
                    className={
                      !props.secondaryActions
                        ? `content-between ${isMobile && props.isResponsiveAction ? 'flex-column' : 'flex-row'}`
                        : ''
                    }
                  >
                    {props.backAction ? (
                      <div className="mr-8">
                        <Button icon={ArrowLeftIcon} variant="tertiary" onClick={props.backAction} />
                      </div>
                    ) : null}
                    <div className={`d-flex ${props.isTitleFullWidth ? 'w-full' : ''}`}>
                      <BoldText className={'custom-card-title ' + (props.textClassName || '')}>
                        {props.title} {props.required ? <Required /> : null}
                      </BoldText>
                      {props.badgeHeader ? <div className="ml-4">{props.badgeHeader}</div> : null}
                    </div>
                    {props.titleContentRight ? props.titleContentRight : null}
                  </div>
                ) : null}
                <div className="d-flex align-center">
                  {props.secondaryActions ? (
                    <div className="custom-card-btn">
                      {props.secondaryActions && (
                        <Button onClick={props.secondaryActions.onAction} {...props.secondaryActions.buttonProps}>
                          {props.secondaryActions?.content}
                        </Button>
                      )}
                    </div>
                  ) : null}
                  {!!props.collapsible && (
                    <div className="ml-8 cursor-pointer" onClick={() => setIsCollapsed(!isCollapsed)}>
                      <Icon source={isCollapsed ? ChevronDownIcon : ChevronUpIcon} tone="base" />
                    </div>
                  )}
                </div>
              </div>
              <Collapsible open={isCollapsed} id="basic-collapsible" transition={{ duration: '500ms' }} expandOnPrint>
                <div className="custom-card-content">{props.children}</div>
              </Collapsible>
            </Card>
          </div>
        </>
      ) : (
        <>
          <div className="custom-card-header">
            <BoldText>{props.title}</BoldText>
          </div>
          <div className="custom-card-content">{props.children}</div>
        </>
      )}
    </CardStyled>
  );
};

export default memo(CustomCard);
